/**
 * Typed fetch wrapper for the Vitals backend.
 *
 * Every call is same-origin and credentialed (session cookie). A 401 means the
 * session lapsed: we bounce to /login once (never when already there, so the
 * login page can surface its own 401 as an "invalid credentials" message).
 */

import type { MetricAgg } from './metrics.js';
import { feedSSE } from './lib/sseParse.js';
import { safeNext } from './lib/safeNext.js';

export type MetricBucket = 'raw' | 'hour' | 'day';

export interface User {
  id: string;
  email: string;
}
export interface MetricPoint {
  t: string;
  v: number;
}
export interface MetricSeriesResponse {
  type: string;
  bucket: MetricBucket;
  agg: MetricAgg;
  points: MetricPoint[];
}
export interface LatestPoint {
  t: string;
  v: number;
  source: string;
}
export type LatestMap = Record<string, LatestPoint | null>;
export interface SleepSession {
  id: string;
  startedAt: string;
  endedAt: string;
  durationSec: number | null;
  stages: unknown;
  source: string;
}
export interface Workout {
  id: string;
  type: string | null;
  startedAt: string | null;
  endedAt: string | null;
  durationSec: number | null;
  distanceM: number | null;
  avgHr: number | null;
  maxHr: number | null;
  activeCalories: number | null;
  source: string;
}

export interface WorkSession {
  id: string;
  externalId: number;
  date: string;
  startedAt: string | null;
  endedAt: string | null;
  hours: number | null;
  category: string | null;
  notes: string | null;
}

// --- Nutrition -------------------------------------------------------------

export type DraftSource = 'manual' | 'ai_estimate' | 'barcode' | 'mealie';

export interface MealItemDraft {
  name: string;
  qty: number | null;
  unit: string | null;
  calories: number | null;
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
  fiber_g?: number | null;
  sugar_g?: number | null;
  caffeine_mg: number | null;
  confidence?: number | null;
  source: DraftSource;
}

export interface StoredMealItem {
  id: string;
  name: string;
  qty: number | null;
  unit: string | null;
  calories: number | null;
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
  fiber_g: number | null;
  sugar_g: number | null;
  caffeine_mg: number | null;
}

export interface StoredMeal {
  id: string;
  eatenAt: string;
  name: string | null;
  method: string | null;
  notes: string | null;
  items: StoredMealItem[];
}

export interface NutritionSummary {
  date: string;
  tz: number;
  calories: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  fiber_g: number;
  sugar_g: number;
  caffeine_mg: number;
}

/** A previously-logged food, for one-tap repeat logging (portion memory). */
export interface RecentFood {
  key: string;
  name: string;
  brand: string | null;
  lastQty: number | null;
  lastUnit: string | null;
  calories: number | null;
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
  fiber_g: number | null;
  sugar_g: number | null;
  caffeine_mg: number | null;
  timesLogged: number;
  lastLoggedAt: string;
}

/** One local calendar day's nutrition rollup (days with no meals are omitted). */
export interface NutritionDay {
  date: string;
  calories: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  fiber_g: number;
  sugar_g: number;
  caffeine_mg: number;
  mealCount: number;
}

/** Daily nutrition targets (all optional): floors/goals, plus sugar as a ceiling. */
export interface NutritionTargets {
  calories?: number;
  protein_g?: number;
  fiber_g?: number;
  sugar_g?: number;
}

export interface ParseResponse {
  source: string;
  found?: boolean;
  enabled?: boolean;
  items: MealItemDraft[];
}

export interface MealieRecipe {
  id: string;
  slug: string;
  name: string;
}

export interface ParseRequest {
  text?: string;
  imageBase64?: string;
  imageMediaType?: string;
  barcode?: string;
  mealieRecipe?: { slug: string; servings?: number };
}

export interface SaveMealRequest {
  at?: string;
  name?: string;
  method?: string;
  items: MealItemDraft[];
}

export type QuicklogInput =
  | { kind: 'caffeine'; preset?: 'coffee' | 'espresso' | 'energy_drink'; mg?: number }
  | { kind: 'water'; ml: number }
  | { kind: 'alcohol'; units: number }
  | { kind: 'weight'; kg: number }
  | { kind: 'bp'; systolic: number; diastolic: number };

/** Thrown for any non-2xx response other than a handled 401 redirect. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Build a `?a=1&b=2` string, dropping undefined/null values. Exported for tests. */
export function buildQuery(params: Record<string, string | number | undefined | null>): string {
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    usp.set(key, String(value));
  }
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Redirect to the login page unless we are already on it. A page `safeNext`
 * would honour (a Signal `/brief/<id>` link) rides along as `?next=`, so
 * signing in lands back on it. Exported for tests.
 */
export function redirectToLogin(): void {
  const { pathname, search } = window.location;
  if (pathname.startsWith('/login')) return;
  const here = `${pathname}${search ?? ''}`;
  window.location.assign(safeNext(here) ? `/login?next=${encodeURIComponent(here)}` : '/login');
}

export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // A FormData body must NOT get an explicit content-type — the browser sets the
  // multipart boundary. Otherwise a JSON body carries application/json even when
  // a caller passes its own headers object.
  const isForm = typeof FormData !== 'undefined' && init?.body instanceof FormData;
  const res = await fetch(`/api${path}`, {
    ...init,
    credentials: 'include',
    headers:
      init?.body !== undefined && !isForm
        ? { 'content-type': 'application/json', ...init?.headers }
        : init?.headers,
  });

  if (res.status === 401) {
    redirectToLogin();
    throw new ApiError(401, 'unauthorized');
  }
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) message = body.error;
    } catch {
      // non-JSON error body; keep statusText
    }
    throw new ApiError(res.status, message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// --- Auth ------------------------------------------------------------------

export function login(email: string, password: string): Promise<{ user: User }> {
  return request('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) });
}
export function me(): Promise<{ user: User }> {
  return request('/me');
}
export function logout(): Promise<void> {
  return request('/auth/logout', { method: 'POST' });
}

// --- Metrics ---------------------------------------------------------------

export interface MetricsParams {
  type: string;
  from: string;
  to: string;
  bucket?: MetricBucket;
  agg?: MetricAgg;
  source?: string;
}
export function getMetrics(params: MetricsParams): Promise<MetricSeriesResponse> {
  return request(`/metrics${buildQuery({ ...params })}`);
}
export function getLatest(types: string[]): Promise<LatestMap> {
  return request(`/metrics/latest${buildQuery({ types: types.join(',') })}`);
}

// --- Domain ----------------------------------------------------------------

export async function getSleep(from: string, to: string): Promise<SleepSession[]> {
  const res = await request<{ sessions: SleepSession[] }>(`/sleep${buildQuery({ from, to })}`);
  return res.sessions;
}
export async function getLatestSleep(): Promise<SleepSession | null> {
  const res = await request<{ latest: SleepSession | null }>('/sleep/latest');
  return res.latest;
}
export async function getWorkouts(from: string, to: string): Promise<Workout[]> {
  const res = await request<{ workouts: Workout[] }>(`/workouts${buildQuery({ from, to })}`);
  return res.workouts;
}
export async function getWorkSessions(from: string, to: string): Promise<WorkSession[]> {
  const res = await request<{ sessions: WorkSession[] }>(`/work/sessions${buildQuery({ from, to })}`);
  return res.sessions;
}
export function quicklog(input: QuicklogInput): Promise<{ created: unknown[] }> {
  return request('/quicklog', { method: 'POST', body: JSON.stringify(input) });
}

// --- Nutrition -------------------------------------------------------------

export function parseNutrition(body: ParseRequest): Promise<ParseResponse> {
  return request('/nutrition/parse', { method: 'POST', body: JSON.stringify(body) });
}
export async function getMealieRecipes(
  q: string,
): Promise<{ enabled?: boolean; recipes: MealieRecipe[] }> {
  return request(`/nutrition/mealie/recipes${buildQuery({ q })}`);
}
export async function getMeals(from: string, to: string): Promise<StoredMeal[]> {
  const res = await request<{ meals: StoredMeal[] }>(`/meals${buildQuery({ from, to })}`);
  return res.meals;
}
export function saveMeal(body: SaveMealRequest): Promise<{ id: string; metricsWritten: number }> {
  return request('/meals', { method: 'POST', body: JSON.stringify(body) });
}
export function getNutritionSummary(date?: string, tz?: number): Promise<NutritionSummary> {
  return request(`/nutrition/summary${buildQuery({ date, tz })}`);
}
export async function getRecentFoods(limit = 24): Promise<RecentFood[]> {
  const res = await request<{ items: RecentFood[] }>(`/foods/recent${buildQuery({ limit })}`);
  return res.items;
}
export async function getNutritionDays(from: string, to: string, tz?: number): Promise<NutritionDay[]> {
  const res = await request<{ days: NutritionDay[] }>(`/nutrition/days${buildQuery({ from, to, tz })}`);
  return res.days;
}

// --- Training --------------------------------------------------------------

export type PlanDistance = '5k' | '10k' | 'half' | 'full';
export type PlanPhase = 'base' | 'build' | 'peak' | 'taper';

export interface PlanMeta {
  id: string;
  name: string | null;
  distance: string;
  prSeconds: number | null;
  goalSeconds: number | null;
  weeks: number;
  daysPerWeek: number;
  startDate: string;
  createdAt: string;
}

/** A day inside the stored plan blob (weeks[].days[]). */
export interface PlanBlobDay {
  date: string;
  type: string;
  description: string;
  targetPaceSecPerKm?: number;
  distanceKm?: number;
  tag: string;
  isRun: boolean;
}
export interface PlanBlobWeek {
  phase: PlanPhase;
  phaseName: string;
  weekNumber: number;
  totalKm: number;
  days: PlanBlobDay[];
}
export interface PlanBlob {
  meta: {
    distanceLabel: string;
    goalPaceStr: string;
    vdot: number;
    goalVdot: number;
    feasibility: string;
    weeks: number;
    daysPerWeek: number;
  };
  weeks: PlanBlobWeek[];
}

/** One runnable day's tracking row (matched to a blob day by date). */
export interface PlanWorkout {
  id: string;
  date: string;
  phase: string | null;
  type: string | null;
  description: string | null;
  targetPaceSecPerKm: number | null;
  distanceKm: number | null;
  completed: boolean;
  completedSource: string | null;
  workoutId: string | null;
}

export interface PlanDetail extends PlanMeta {
  plan: PlanBlob;
  planWorkouts: PlanWorkout[];
}

export interface CreatePlanBody {
  name?: string;
  distance: PlanDistance;
  prSeconds: number;
  goalSeconds: number;
  weeks: number;
  daysPerWeek: number;
  startDate: string;
}

export interface DistancePR {
  distance: PlanDistance;
  label: string;
  timeSec: number;
  distanceM: number;
  pacePerKm: number;
  vdot: number;
  date: string;
  workoutId: string;
}
export interface VdotPoint {
  date: string;
  vdot: number;
  distance: PlanDistance;
}
export interface TrainingStats {
  prs: DistancePR[];
  vdotTrend: VdotPoint[];
  adherence: {
    planId: string;
    planName: string | null;
    completed: number;
    pastTotal: number;
    percent: number;
  } | null;
}

export async function getPlans(): Promise<PlanMeta[]> {
  const res = await request<{ plans: PlanMeta[] }>('/plans');
  return res.plans;
}
export function createPlan(body: CreatePlanBody): Promise<PlanDetail> {
  return request('/plans', { method: 'POST', body: JSON.stringify(body) });
}
export function getPlan(id: string): Promise<PlanDetail> {
  return request(`/plans/${id}`);
}
export function checkoffPlanDay(
  planId: string,
  planWorkoutId: string,
  completed: boolean,
): Promise<{ workout: PlanWorkout }> {
  return request(`/plans/${planId}/checkoff`, {
    method: 'PUT',
    body: JSON.stringify({ planWorkoutId, completed }),
  });
}
export function deletePlan(id: string): Promise<void> {
  return request(`/plans/${id}`, { method: 'DELETE' });
}
export function getTrainingStats(): Promise<TrainingStats> {
  return request('/training/stats');
}

// --- Records: labs ---------------------------------------------------------

export interface LabResultDraft {
  marker: string;
  rawName?: string;
  value: number | null;
  unit: string | null;
  refLow: number | null;
  refHigh: number | null;
}

export interface LabDraft {
  fileId: string;
  panelDate: string | null;
  provider: string | null;
  results: LabResultDraft[];
  extractionDisabled?: boolean;
  /** Server hint shown on empty drafts (HEIC skip, extraction disabled, …). */
  note?: string;
}

export interface StoredLabResult {
  id: string;
  marker: string;
  value: number | null;
  textValue: string | null;
  unit: string | null;
  refLow: number | null;
  refHigh: number | null;
}

export interface StoredLabPanel {
  id: string;
  panelDate: string;
  provider: string | null;
  sourceFileId: string | null;
  createdAt: string;
  results: StoredLabResult[];
}

export interface MarkerPoint {
  date: string;
  value: number | null;
  unit: string | null;
  refLow: number | null;
  refHigh: number | null;
}

export interface ConfirmLabsBody {
  fileId?: string;
  panelDate: string;
  provider?: string | null;
  results: LabResultDraft[];
}

/** Upload a lab report (PDF/image) and get an unsaved extraction draft. */
export async function uploadLab(file: File): Promise<LabDraft> {
  const form = new FormData();
  form.append('file', file);
  return request('/labs', { method: 'POST', body: form });
}
export function confirmLabs(body: ConfirmLabsBody): Promise<{ id: string }> {
  return request('/labs/confirm', { method: 'POST', body: JSON.stringify(body) });
}
export async function getLabPanels(): Promise<StoredLabPanel[]> {
  const res = await request<{ panels: StoredLabPanel[] }>('/labs');
  return res.panels;
}
export function getMarkerHistory(marker: string): Promise<{ marker: string; points: MarkerPoint[] }> {
  return request(`/labs/markers/${encodeURIComponent(marker)}`);
}

// --- Records: notes --------------------------------------------------------

export interface StoredNote {
  id: string;
  noteDate: string;
  provider: string | null;
  type: string | null;
  text: string | null;
  attachments: string[];
  createdAt: string;
}

export interface AddNoteBody {
  noteDate?: string;
  provider?: string;
  type?: string;
  text?: string;
  file?: File | null;
}

export async function getNotes(): Promise<StoredNote[]> {
  const res = await request<{ notes: StoredNote[] }>('/notes');
  return res.notes;
}
export function addNote(body: AddNoteBody): Promise<{ id: string }> {
  if (body.file) {
    const form = new FormData();
    if (body.noteDate) form.append('noteDate', body.noteDate);
    if (body.provider) form.append('provider', body.provider);
    if (body.type) form.append('type', body.type);
    if (body.text) form.append('text', body.text);
    form.append('file', body.file);
    return request('/notes', { method: 'POST', body: form });
  }
  const { file: _file, ...json } = body;
  return request('/notes', { method: 'POST', body: JSON.stringify(json) });
}

// --- Records: medications --------------------------------------------------

export interface StoredMed {
  id: string;
  name: string;
  dose: string | null;
  schedule: unknown;
  active: boolean;
  notes: string | null;
  createdAt: string;
  lastTakenAt: string | null;
}

export interface MedBody {
  name?: string;
  dose?: string | null;
  schedule?: unknown;
  active?: boolean;
  notes?: string | null;
}

export async function getMeds(): Promise<StoredMed[]> {
  const res = await request<{ meds: StoredMed[] }>('/meds');
  return res.meds;
}
export function addMed(body: MedBody): Promise<{ id: string }> {
  return request('/meds', { method: 'POST', body: JSON.stringify(body) });
}
export function updateMed(id: string, body: MedBody): Promise<{ id: string }> {
  return request(`/meds/${id}`, { method: 'PUT', body: JSON.stringify(body) });
}
export function logMedIntake(id: string, at?: string): Promise<{ id: string; takenAt: string }> {
  return request(`/meds/${id}/intake`, { method: 'POST', body: JSON.stringify(at ? { at } : {}) });
}

// --- Journal ---------------------------------------------------------------

export interface StoredJournalEntry {
  id: string;
  entryDate: string;
  mood: number | null;
  energy: number | null;
  symptoms: string[];
  tags: string[];
  text: string | null;
  createdAt: string;
}

export interface JournalBody {
  at?: string;
  mood?: number | null;
  energy?: number | null;
  symptoms?: string[];
  tags?: string[];
  text?: string | null;
}

export async function getJournal(): Promise<StoredJournalEntry[]> {
  const res = await request<{ entries: StoredJournalEntry[] }>('/journal');
  return res.entries;
}
export function addJournalEntry(body: JournalBody): Promise<{ id: string }> {
  return request('/journal', { method: 'POST', body: JSON.stringify(body) });
}

// --- Settings --------------------------------------------------------------

export async function getSettings(): Promise<Record<string, unknown>> {
  const res = await request<{ settings: Record<string, unknown> }>('/settings');
  return res.settings;
}
export function putSetting(key: string, value: unknown): Promise<{ key: string; value: unknown }> {
  return request('/settings', { method: 'PUT', body: JSON.stringify({ key, value }) });
}

// --- Google Health (Fitbit data; the Fitbit Web API is gone Sept 2026) ------

export interface FitbitStatus {
  enabled: boolean;
  connected: boolean;
  disconnected: boolean;
  lastSyncAt: string | null;
  cursor: string | null;
  nextAllowedAt: string | null;
}

export interface FitbitSyncSummary {
  ran: boolean;
  reason?: string;
  /** Data types covered by the run (the old Fitbit worker counted days). */
  typesSynced: number;
  cursor?: string | null;
  inserted: { metrics: number; sleep: number };
}

export function getFitbitStatus(): Promise<FitbitStatus> {
  return request('/ghealth/status');
}

// --- Sync health -----------------------------------------------------------

export interface SyncSource {
  source: string;
  lastSyncAt: string | null;
  lastError: string | null;
  lastReceivedAt: string | null;
}

export function getSyncStatus(): Promise<{ sources: SyncSource[] }> {
  return request('/sync/status');
}

// --- Coach -----------------------------------------------------------------

export interface CoachConversation {
  id: string;
  title: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CoachMessage {
  id: string;
  role: string;
  content: string;
  meta: unknown;
  createdAt: string;
}

export async function getCoachConversations(): Promise<CoachConversation[]> {
  const res = await request<{ conversations: CoachConversation[] }>('/coach/conversations');
  return res.conversations;
}

export async function createCoachConversation(): Promise<CoachConversation> {
  const res = await request<{ conversation: CoachConversation }>('/coach/conversations', {
    method: 'POST',
    body: JSON.stringify({}),
  });
  return res.conversation;
}

export async function getCoachMessages(
  conversationId: string,
): Promise<{ conversation: CoachConversation; messages: CoachMessage[] }> {
  return request(`/coach/conversations/${conversationId}/messages`);
}

export function deleteCoachConversation(conversationId: string): Promise<void> {
  return request(`/coach/conversations/${conversationId}`, { method: 'DELETE' });
}

export interface CoachStreamHandlers {
  onDelta: (text: string) => void;
  onDone: (messageId: string) => void;
  onError: (message: string) => void;
}

/**
 * POST a coach message and consume the SSE reply stream (EventSource can't POST,
 * so we read the fetch ReadableStream ourselves). Resolves when the stream ends.
 * A 501 throws ApiError(501) so the caller can show a "not configured" notice;
 * an `AbortSignal` cancels the request (the read loop rejects with AbortError).
 */
export async function streamCoachMessage(
  conversationId: string,
  text: string,
  handlers: CoachStreamHandlers,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch('/api/coach/messages', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ conversationId, text }),
    signal,
  });

  if (res.status === 401) {
    redirectToLogin();
    throw new ApiError(401, 'unauthorized');
  }
  if (!res.ok || !res.body) {
    let message = res.statusText;
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) message = body.error;
    } catch {
      // keep statusText
    }
    throw new ApiError(res.status, message);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    const parsed = feedSSE(buffer, decoder.decode(value, { stream: true }));
    buffer = parsed.buffer;
    for (const ev of parsed.events) {
      let data: { text?: string; messageId?: string; message?: string } = {};
      try {
        data = ev.data ? JSON.parse(ev.data) : {};
      } catch {
        continue;
      }
      if (ev.event === 'delta') handlers.onDelta(data.text ?? '');
      else if (ev.event === 'done') handlers.onDone(data.messageId ?? '');
      else if (ev.event === 'error') handlers.onError(data.message ?? 'coach error');
    }
  }
}
// --- Concerns --------------------------------------------------------------

export interface SymptomTag {
  id: string;
  name: string;
}
export interface ConcernNote {
  id: string;
  concernId: string;
  notedAt: string;
  text: string | null;
  severity: number | null;
  symptoms: SymptomTag[];
}
/** One cited assistant assessment written onto a concern's timeline. */
export interface AssistantNote {
  id: string;
  concernId: string | null;
  body: string;
  citations: Array<{ tool: string; summary: string }>;
  createdAt: string;
}
/** The assistant's closing summary of a resolved episode. */
export interface EpisodeSummary {
  concernId: string;
  symptoms: string[];
  durationDays: number | null;
  contributors: Array<{ factor: string; confidence: 'low' | 'medium' | 'high' }>;
  outcome: string | null;
  narrative: string;
  createdAt: string;
}
export interface Concern {
  id: string;
  title: string;
  openedAt: string;
  closedAt: string | null;
  outcome: string | null;
  notes: ConcernNote[];
  /** Assistant assessments on this concern's timeline, chronological. */
  assessments: AssistantNote[];
  /** Closing episode summary (closed concerns the assistant has summarized). */
  summary: EpisodeSummary | null;
}
export interface ConcernBand {
  id: string;
  title: string;
  openedAt: string;
  closedAt: string | null;
}

export type ConcernStatus = 'open' | 'closed' | 'all';

export async function getConcerns(status: ConcernStatus = 'all'): Promise<Concern[]> {
  const res = await request<{ concerns: Concern[] }>(`/concerns${buildQuery({ status })}`);
  return res.concerns;
}
export async function createConcern(body: {
  title: string;
  note?: string;
  symptoms?: string[];
  severity?: number;
}): Promise<Concern> {
  const res = await request<{ concern: Concern }>('/concerns', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return res.concern;
}
export async function addConcernNote(
  id: string,
  body: { text?: string; symptoms?: string[]; severity?: number },
): Promise<ConcernNote> {
  const res = await request<{ note: ConcernNote }>(`/concerns/${id}/notes`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return res.note;
}
export async function closeConcern(id: string, outcome?: string): Promise<Concern> {
  const res = await request<{ concern: Concern }>(`/concerns/${id}/close`, {
    method: 'PUT',
    body: JSON.stringify(outcome !== undefined ? { outcome } : {}),
  });
  return res.concern;
}
export async function getConcernBands(from: string, to: string): Promise<ConcernBand[]> {
  const res = await request<{ bands: ConcernBand[] }>(`/concerns/bands${buildQuery({ from, to })}`);
  return res.bands;
}
export async function searchSymptoms(q: string): Promise<SymptomTag[]> {
  const res = await request<{ symptoms: SymptomTag[] }>(`/symptoms${buildQuery({ q })}`);
  return res.symptoms;
}

// --- Assistant -------------------------------------------------------------

/** One row of the assistant's run log (Admin observability). */
export interface AssistantRun {
  id: string;
  trigger: string;
  status: string;
  model: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  toolCalls: number | null;
  error: string | null;
  createdAt: string;
  finishedAt: string | null;
  /** Human string derived from the run's context (e.g. the concern title). */
  contextLabel: string;
}

export async function getAssistantRuns(limit = 50): Promise<AssistantRun[]> {
  const res = await request<{ runs: AssistantRun[] }>(`/assistant/runs${buildQuery({ limit })}`);
  return res.runs;
}

/** Kick a run by hand (admin/testing). Returns the new run's id (202). */
export function runAssistant(body: {
  trigger: 'weekly' | 'manual';
  concernId?: string;
}): Promise<{ runId: string }> {
  return request('/assistant/run', { method: 'POST', body: JSON.stringify(body) });
}

// --- Alerts ----------------------------------------------------------------

export interface Alert {
  id: string;
  ruleId: string;
  firedAt: string;
  message: string | null;
  acknowledged: boolean;
  meta: unknown;
}

export async function getAlerts(unacked = false): Promise<Alert[]> {
  const res = await request<{ alerts: Alert[] }>(`/alerts${buildQuery({ unacked: unacked ? 1 : undefined })}`);
  return res.alerts;
}

export async function ackAlert(id: string): Promise<Alert> {
  const res = await request<{ alert: Alert }>(`/alerts/${id}/ack`, { method: 'PUT' });
  return res.alert;
}

// --- Reports ---------------------------------------------------------------

export function triggerWeeklyReport(): Promise<{ conversationId: string; usedAi: boolean }> {
  return request('/reports/weekly', { method: 'POST' });
}

// --- Insights: baselines, cards, records -----------------------------------

export interface Baseline {
  mean: number;
  sd: number;
  n: number;
  date: string;
}

export interface InsightCard {
  id: string;
  kind: 'correlation' | 'record' | 'pattern' | 'weekly';
  title: string;
  body: string;
  data: unknown;
  computedAt: string;
  dismissedAt: string | null;
  rating: number | null;
}

export interface MetricRecord {
  max: { v: number; date: string };
  min: { v: number; date: string };
  total: number;
}

/** Latest rolling baseline per type (null for a type with no baseline yet). */
export async function getBaselines(types: string[]): Promise<Record<string, Baseline | null>> {
  const res = await request<{ baselines: Record<string, Baseline | null> }>(
    `/baselines${buildQuery({ types: types.join(',') })}`,
  );
  return res.baselines;
}

export async function getInsights(includeDismissed = false): Promise<InsightCard[]> {
  const res = await request<{ cards: InsightCard[] }>(
    `/insights${buildQuery({ includeDismissed: includeDismissed ? 1 : undefined })}`,
  );
  return res.cards;
}

export async function dismissInsight(id: string): Promise<InsightCard> {
  const res = await request<{ card: InsightCard }>(`/insights/${id}/dismiss`, { method: 'PUT' });
  return res.card;
}

export async function rateInsight(id: string, rating: 1 | -1): Promise<InsightCard> {
  const res = await request<{ card: InsightCard }>(`/insights/${id}/rate`, {
    method: 'PUT',
    body: JSON.stringify({ rating }),
  });
  return res.card;
}

/** All-time day-bucketed extremes + tracked-day count per type (null if none). */
export async function getMetricRecords(
  types: string[],
): Promise<Record<string, MetricRecord | null>> {
  const res = await request<{ records: Record<string, MetricRecord | null> }>(
    `/stats/records${buildQuery({ types: types.join(',') })}`,
  );
  return res.records;
}

// --- Admin -----------------------------------------------------------------

export interface IngestEvent {
  id: string;
  source: string;
  receivedAt: string;
  bytes: number | null;
  inserted: { metrics: number; sleep: number; workouts: number } | null;
  skippedCount: number | null;
  durationMs: number | null;
}

export interface AiUsageCall {
  id: string;
  at: string;
  feature: string;
  model: string;
  inputTokens: number | null;
  outputTokens: number | null;
  estCostUsd: number | null;
  durationMs: number | null;
}

export interface AiFeatureBreakdown {
  feature: string;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
}

export interface AiDayBreakdown {
  day: string;
  calls: number;
  costUsd: number;
}

export interface AiUsageSummary {
  calls: number;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  byFeature: AiFeatureBreakdown[];
  byDay: AiDayBreakdown[];
}

export interface AdminSystem {
  dbRows: Record<string, number>;
  dbSizePretty: string | null;
  lastBackup: string | null;
  lastFitbitSync: string | null;
  lastIngest: string | null;
}

export async function getIngestEvents(
  opts: { limit?: number; before?: string } = {},
): Promise<IngestEvent[]> {
  const res = await request<{ events: IngestEvent[] }>(
    `/admin/ingest-events${buildQuery({ limit: opts.limit, before: opts.before })}`,
  );
  return res.events;
}

export function getAiUsage(
  month?: string,
): Promise<{ month: string; summary: AiUsageSummary; calls: AiUsageCall[] }> {
  return request(`/admin/ai-usage${buildQuery({ month })}`);
}

export function getAdminSystem(): Promise<AdminSystem> {
  return request('/admin/system');
}

export function connectFitbit(): Promise<{ authUrl: string }> {
  return request('/ghealth/connect', { method: 'POST' });
}
export function syncFitbit(): Promise<FitbitSyncSummary> {
  return request('/ghealth/sync', { method: 'POST' });
}
export function disconnectFitbit(): Promise<{ ok: boolean }> {
  return request('/ghealth/disconnect', { method: 'POST' });
}

// --- Intent layer: goals, routines, calendar, nudges ------------------------

export type GoalKind = 'metric' | 'habit' | 'milestone' | 'open';
export type GoalStatus = 'active' | 'paused' | 'achieved' | 'abandoned';
export type GoalDirection = 'up' | 'down' | 'maintain';

export interface Goal {
  id: string;
  title: string;
  detail: string | null;
  domain: string;
  kind: GoalKind;
  metricType: string | null;
  targetValue: number | null;
  baselineValue: number | null;
  direction: GoalDirection | null;
  unit: string | null;
  startDate: string;
  targetDate: string | null;
  status: GoalStatus;
  priority: number;
  motivation: string | null;
  achievedAt: string | null;
}

/** Progress for a metric goal, as computed server-side. */
export interface GoalMetricProgress {
  fraction: number;
  timeFraction: number | null;
  expectedValue: number | null;
  paceDelta: number | null;
  status: 'ahead' | 'on_track' | 'behind' | 'achieved' | 'unknown';
  remaining: number;
  daysRemaining: number | null;
  requiredRatePerDay: number | null;
  observedRatePerDay: number | null;
  projectedDate: string | null;
  currentValue: number | null;
  observedAt: string | null;
}

/** Adherence for a habit goal over the trailing window. */
export interface GoalHabitProgress {
  settled: number;
  done: number;
  skipped: number;
  missed: number;
  rate: number;
  streak: number;
  status: 'strong' | 'slipping' | 'broken' | 'unknown';
  routineCount: number;
  windowDays: number;
}

export interface GoalWithProgress {
  goal: Goal;
  metric: GoalMetricProgress | null;
  habit: GoalHabitProgress | null;
  summary: string;
}

export interface Routine {
  id: string;
  goalId: string | null;
  title: string;
  detail: string | null;
  domain: string;
  schedule: unknown;
  timezone: string;
  active: boolean;
  startsOn: string;
  endsOn: string | null;
  remind: boolean;
}

export type SlotState = 'upcoming' | 'due' | 'missed' | 'done' | 'skipped';

export interface RoutineOccurrence {
  routineId: string;
  routineTitle: string;
  goalId: string | null;
  domain: string;
  dueAt: string;
  localDate: string;
  localTime: string;
  state: SlotState;
  note: string | null;
}

export type Recurrence = 'none' | 'yearly' | 'monthly' | 'weekly' | 'daily';

export interface LifeEvent {
  id: string;
  title: string;
  detail: string | null;
  domain: string;
  kind: string;
  startsAt: string;
  endsAt: string | null;
  allDay: boolean;
  timezone: string;
  location: string | null;
  people: string[];
  recurrence: Recurrence;
  prepHorizonDays: number;
  importance: number;
}

export interface EventOccurrence {
  eventId: string;
  title: string;
  kind: string;
  detail: string | null;
  location: string | null;
  people: string[];
  importance: number;
  prepHorizonDays: number;
  allDay: boolean;
  startsAt: string;
  endsAt: string | null;
  daysUntil: number;
  /** Month-calendar extras (server ≥ 2026-10-15): the series, the job, the project, sync state. */
  recurrence?: Recurrence;
  timezone?: string;
  source?: string;
  sphere?: string;
  projectId?: string | null;
  /** On a synced calendar: deleting it here answers 409 ("delete it there"). */
  onRemote?: boolean;
}

export interface Nudge {
  id: string;
  source: string;
  sourceId: string | null;
  dedupeKey: string;
  title: string;
  body: string | null;
  domain: string;
  priority: number;
  dueAt: string;
  expiresAt: string | null;
  action: { kind?: string; routineId?: string; dueAt?: string; path?: string } | null;
  snoozedUntil: string | null;
}

export interface Agenda {
  now: string;
  localDate: string;
  nudges: Nudge[];
  routines: RoutineOccurrence[];
  goals: GoalWithProgress[];
  events: Array<{
    id: string;
    title: string;
    kind: string;
    startsAt: string;
    daysUntil: number;
    location: string | null;
    people: string[];
    detail: string | null;
  }>;
}

export function getAgenda(): Promise<Agenda> {
  return request('/agenda');
}

export async function getGoals(status: GoalStatus | 'all' = 'active'): Promise<GoalWithProgress[]> {
  const res = await request<{ goals: GoalWithProgress[] }>(
    `/goals${status === 'all' ? '' : buildQuery({ status })}`,
  );
  return res.goals;
}

export async function createGoal(body: Partial<Goal> & { title: string }): Promise<Goal> {
  const res = await request<{ goal: Goal }>('/goals', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return res.goal;
}

export async function updateGoal(id: string, body: Partial<Goal>): Promise<Goal> {
  const res = await request<{ goal: Goal }>(`/goals/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  return res.goal;
}

export function deleteGoal(id: string): Promise<void> {
  return request(`/goals/${id}`, { method: 'DELETE' });
}

export async function getRoutines(activeOnly = true): Promise<Routine[]> {
  const res = await request<{ routines: Routine[] }>(
    `/routines${buildQuery({ active: activeOnly ? '1' : undefined })}`,
  );
  return res.routines;
}

export async function getRoutineOccurrences(): Promise<RoutineOccurrence[]> {
  const res = await request<{ occurrences: RoutineOccurrence[] }>('/routines/occurrences');
  return res.occurrences;
}

export async function createRoutine(body: {
  title: string;
  schedule: unknown;
  detail?: string | null;
  goalId?: string | null;
  domain?: string;
  timezone?: string;
  remind?: boolean;
}): Promise<Routine> {
  const res = await request<{ routine: Routine }>('/routines', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return res.routine;
}

export async function updateRoutine(id: string, body: Partial<Routine>): Promise<Routine> {
  const res = await request<{ routine: Routine }>(`/routines/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  return res.routine;
}

export function deleteRoutine(id: string): Promise<void> {
  return request(`/routines/${id}`, { method: 'DELETE' });
}

/** Settle one occurrence. Omit `dueAt` to settle the slot nearest now. */
export function logRoutine(
  id: string,
  body: { dueAt?: string; status?: 'done' | 'skipped' | 'missed'; note?: string } = {},
): Promise<{ log: { id: string; dueAt: string; status: string } }> {
  return request(`/routines/${id}/log`, { method: 'POST', body: JSON.stringify(body) });
}

export async function getEvents(): Promise<LifeEvent[]> {
  const res = await request<{ events: LifeEvent[] }>('/calendar');
  return res.events;
}

export async function getEvent(id: string): Promise<LifeEvent> {
  return (await request<{ event: LifeEvent }>(`/calendar/${encodeURIComponent(id)}`)).event;
}

/** Recurring events projected into concrete dated occurrences. */
export async function getEventOccurrences(from?: string, to?: string): Promise<EventOccurrence[]> {
  const res = await request<{ occurrences: EventOccurrence[] }>(
    `/calendar${buildQuery({ expand: '1', from, to })}`,
  );
  return res.occurrences;
}

export async function createEvent(body: {
  title: string;
  startsAt: string;
  endsAt?: string | null;
  kind?: string;
  detail?: string | null;
  location?: string | null;
  people?: string[];
  recurrence?: Recurrence;
  prepHorizonDays?: number;
  importance?: number;
  allDay?: boolean;
  timezone?: string;
  domain?: string;
  meta?: unknown;
}): Promise<LifeEvent> {
  const res = await request<{ event: LifeEvent }>('/calendar', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return res.event;
}

/** `sphereKey` / `projectId` re-file the event; the server merges them into its meta. */
export type EventPatch = Partial<LifeEvent> & { sphereKey?: string | null; projectId?: string | null };

export async function updateEvent(id: string, body: EventPatch): Promise<LifeEvent> {
  const res = await request<{ event: LifeEvent }>(`/calendar/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  return res.event;
}

export function deleteEvent(id: string): Promise<void> {
  return request(`/calendar/${id}`, { method: 'DELETE' });
}

export async function getNudges(): Promise<Nudge[]> {
  const res = await request<{ nudges: Nudge[] }>('/nudges');
  return res.nudges;
}

export function actNudge(id: string): Promise<{ nudge: Nudge }> {
  return request(`/nudges/${id}/act`, { method: 'POST', body: JSON.stringify({}) });
}
export function dismissNudge(id: string): Promise<{ nudge: Nudge }> {
  return request(`/nudges/${id}/dismiss`, { method: 'POST', body: JSON.stringify({}) });
}
export function snoozeNudge(id: string, minutes: number): Promise<{ nudge: Nudge }> {
  return request(`/nudges/${id}/snooze`, { method: 'POST', body: JSON.stringify({ minutes }) });
}

// --- Finance ---------------------------------------------------------------

export interface FinanceAccount {
  id: string;
  name: string;
  institution: string | null;
  type: string;
  currency: string;
  mask: string | null;
  balanceCents: number | null;
  balanceAt: string | null;
  source: string;
  active: boolean;
  excludeFromTotals: boolean;
}

export interface CategorySpend {
  category: string;
  cents: number;
  count: number;
}

export interface MerchantSpend {
  merchant: string;
  cents: number;
  count: number;
}

export interface FinanceSummary {
  from: string;
  to: string;
  netWorth: { netCents: number; assetsCents: number; liabilitiesCents: number };
  cashFlow: { inCents: number; outCents: number };
  topCategories: CategorySpend[];
  accountCount: number;
  /** Whether a live bank connection is configured. */
  simplefin: boolean;
}

export interface FinanceTransaction {
  id: string;
  accountId: string;
  postedAt: string;
  amountCents: number;
  description: string;
  merchant: string | null;
  category: string | null;
  categorySource: string | null;
  pending: boolean;
  isTransfer: boolean;
  /** Only on a split row (migration 0025): lines summing exactly to `amountCents`. */
  splits?: TransactionSplitLine[];
}

export interface RecurringCharge {
  merchant: string;
  cents: number;
  count: number;
  lastSeen: string;
  category: string | null;
}

export interface OrderLine {
  id: string;
  source: string;
  orderId: string;
  orderedAt: string;
  title: string;
  quantity: number;
  totalCents: number;
  url: string | null;
}

export function getFinanceSummary(from?: string, to?: string): Promise<FinanceSummary> {
  return request(`/finance/summary${buildQuery({ from, to })}`);
}

export async function getFinanceAccounts(): Promise<FinanceAccount[]> {
  const res = await request<{ accounts: FinanceAccount[] }>('/finance/accounts');
  return res.accounts;
}

export async function createFinanceAccount(body: {
  name: string;
  type?: string;
  institution?: string | null;
  balanceCents?: number | null;
}): Promise<FinanceAccount> {
  const res = await request<{ account: FinanceAccount }>('/finance/accounts', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return res.account;
}

export async function getFinanceTransactions(params: {
  from?: string;
  to?: string;
  category?: string;
  search?: string;
  limit?: number;
} = {}): Promise<FinanceTransaction[]> {
  const res = await request<{ transactions: FinanceTransaction[] }>(
    `/finance/transactions${buildQuery({
      from: params.from,
      to: params.to,
      category: params.category,
      search: params.search,
      limit: params.limit,
    })}`,
  );
  return res.transactions;
}

export async function updateFinanceTransaction(
  id: string,
  body: { category?: string; merchant?: string | null; isTransfer?: boolean },
): Promise<FinanceTransaction> {
  const res = await request<{ transaction: FinanceTransaction }>(`/finance/transactions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  return res.transaction;
}

export function getSpending(
  from?: string,
  to?: string,
): Promise<{ byCategory: CategorySpend[]; byMerchant: MerchantSpend[] }> {
  return request(`/finance/spending${buildQuery({ from, to })}`);
}

export async function getRecurring(days = 180): Promise<RecurringCharge[]> {
  const res = await request<{ recurring: RecurringCharge[] }>(
    `/finance/recurring${buildQuery({ days })}`,
  );
  return res.recurring;
}

export async function getOrders(params: { from?: string; to?: string; search?: string } = {}): Promise<OrderLine[]> {
  const res = await request<{ orders: OrderLine[] }>(`/finance/orders${buildQuery(params)}`);
  return res.orders;
}

export async function getCategories(): Promise<string[]> {
  const res = await request<{ categories: string[] }>('/finance/rules');
  return res.categories;
}

/** Income sources → spending categories + saved, for the cash-flow Sankey. */
export interface CashFlow {
  from: string;
  to: string;
  incomeCents: number;
  spentCents: number;
  /** income − spent; negative when the window spent more than came in. */
  savedCents: number;
  /** `category` marks a filed income category (a job); those never fold into "Other income". */
  sources: Array<{ name: string; cents: number; category?: string; members?: string[] }>;
  /** `members`: what a folded "Other" stands for — payer names (sources) or category paths. */
  categories: Array<{ category: string; cents: number; members?: string[] }>;
}

export function getCashFlow(from?: string, to?: string): Promise<CashFlow> {
  return request(`/finance/flow${buildQuery({ from, to })}`);
}

export interface BankStatus {
  connected: boolean;
  lastSyncAt: string | null;
  /** Last problem SimpleFIN reported (e.g. a bank needing re-login). */
  lastError: string | null;
}

export interface BankSyncResult {
  ran: boolean;
  accounts: number;
  transactions: number;
  errors: string[];
}

export function getBankStatus(): Promise<BankStatus> {
  return request('/finance/simplefin');
}

/** Exchange a SimpleFIN setup token; the server saves it and runs the first sync. */
export function connectBank(setupToken: string): Promise<{ connected: boolean; sync: BankSyncResult | null; error?: string }> {
  return request('/finance/simplefin/claim', { method: 'POST', body: JSON.stringify({ setupToken }) });
}

export function syncBank(): Promise<BankSyncResult> {
  return request('/finance/sync', { method: 'POST' });
}

export function importTransactionsCsv(
  accountId: string,
  csv: string,
): Promise<{ imported: number; parsed: number; skipped: Array<{ line: number; reason: string }> }> {
  return request('/finance/import/transactions', {
    method: 'POST',
    body: JSON.stringify({ accountId, csv }),
  });
}

export function importOrdersCsv(
  source: string,
  csv: string,
): Promise<{ imported: number; parsed: number; skipped: Array<{ line: number; reason: string }> }> {
  return request('/finance/import/orders', {
    method: 'POST',
    body: JSON.stringify({ source, csv }),
  });
}

// --- Money redesign (migration 0022): tree categories, overview, budgets -----

/** One node of the category tree. `path` is the full dotted key. */
export interface CategoryTreeNode {
  path: string;
  key: string;
  label: string;
  children?: CategoryTreeNode[];
}

export async function getCategoryTree(): Promise<CategoryTreeNode[]> {
  const res = await request<{ tree: CategoryTreeNode[] }>('/finance/categories');
  return res.tree;
}

/** The overview groups, in display order. `cash` = checking + savings + cash. */
export type AccountGroupKey = 'cash' | 'credit' | 'investment' | 'vehicle' | 'property' | 'loan';

export interface OverviewAccount extends FinanceAccount {
  /** Balance change since the first day of the current month (null when no snapshot). */
  changeCents: number | null;
}

export interface AccountGroup {
  key: AccountGroupKey;
  label: string;
  liability: boolean;
  /** Assets positive; liabilities as a positive amount owed. */
  totalCents: number;
  changeCents: number | null;
  accounts: OverviewAccount[];
}

export interface BreakdownSide {
  totalCents: number;
  byGroup: Array<{ key: AccountGroupKey; label: string; cents: number; pct: number }>;
}

export interface FinanceOverview {
  netCents: number;
  netChangeCents: number | null;
  groups: AccountGroup[];
  assets: BreakdownSide;
  liabilities: BreakdownSide;
}

export function getFinanceOverview(): Promise<FinanceOverview> {
  return request('/finance/overview');
}

/** Per-group balance series. `values[i]` lines up with `buckets[i]`; liabilities are positive amounts owed. */
export interface BalanceHistory {
  interval: 'day' | 'week' | 'month';
  buckets: string[];
  groups: Array<{ key: AccountGroupKey; label: string; liability: boolean; values: number[] }>;
  net: number[];
}

export function getBalanceHistory(from: string, interval: 'day' | 'week' | 'month'): Promise<BalanceHistory> {
  return request(`/finance/balances${buildQuery({ from, interval })}`);
}

/** A deterministic category guess for a Review row (rule, amount band or merchant history — never a model). */
export interface ReviewSuggestion {
  category: string;
  label: string;
  /** Short why: "8 of 8 past Kroger", "Sam's Club under $60", "rule: kroger". */
  reason: string;
  confidence: 'high' | 'medium' | 'low';
  source: 'rule' | 'band' | 'history';
}

export type ReviewTransaction = FinanceTransaction & { suggestions?: ReviewSuggestion[] };

/** Uncategorized, non-transfer rows from all time, newest first, each with up to 3 suggestions. */
export async function getReviewQueue(): Promise<{ transactions: ReviewTransaction[]; total: number }> {
  return request('/finance/review');
}

/** Set a path; `remember` also writes a merchant rule and files the merchant's other uncategorized rows. */
export function categorizeTransaction(
  id: string,
  body: { category: string; remember?: boolean },
): Promise<{ transaction: FinanceTransaction; alsoFiled: number }> {
  return request(`/finance/transactions/${id}/category`, { method: 'POST', body: JSON.stringify(body) });
}

export interface PhraseResult {
  matches: Array<{ path: string; label: string; score: number }>;
  source: 'keywords' | 'ai' | 'none';
}

/** Words → candidate categories. Keywords first; Claude (subscription) only if nothing matched. */
export function matchCategoryPhrase(text: string, transactionId?: string): Promise<PhraseResult> {
  return request('/finance/categorize/phrase', { method: 'POST', body: JSON.stringify({ text, transactionId }) });
}

/** Spending rolled up to `depth` (1–3), optionally only under one prefix. */
export function getSpendingTree(
  from: string,
  to: string,
  opts: { depth?: number; under?: string } = {},
): Promise<{ byCategory: CategorySpend[]; byMerchant: MerchantSpend[] }> {
  return request(`/finance/spending${buildQuery({ from, to, depth: opts.depth, under: opts.under })}`);
}

export interface MonthlyReport {
  months: string[]; // 'YYYY-MM'
  incomeCents: number[];
  spentCents: number[];
  categories: Array<{ path: string; label: string; cents: number[] }>;
}

export function getMonthlyReport(opts: { months?: number; depth?: number; under?: string } = {}): Promise<MonthlyReport> {
  return request(`/finance/reports/monthly${buildQuery({ months: opts.months, depth: opts.depth, under: opts.under })}`);
}

export interface Budget {
  category: string;
  label: string;
  amountCents: number;
  spentCents: number;
}

export async function getBudgets(month?: string): Promise<{ month: string; budgets: Budget[] }> {
  return request(`/finance/budgets${buildQuery({ month })}`);
}

/** amountCents 0 removes the budget. */
export function setBudget(category: string, amountCents: number): Promise<{ ok: true }> {
  return request('/finance/budgets', { method: 'PUT', body: JSON.stringify({ category, amountCents }) });
}

export interface AdviceItem {
  key: string;
  level: 'good' | 'warn' | 'todo';
  title: string;
  detail: string;
}

export async function getAdvice(): Promise<AdviceItem[]> {
  const res = await request<{ advice: AdviceItem[] }>('/finance/advice');
  return res.advice;
}

/** One account's reconcile check: does the bank balance equal the ledger since the anchor day? */
export interface ReconcileAccount {
  accountId: string;
  name: string;
  source: string;              // 'simplefin' | 'mercury' | 'manual'
  syncedAt: string | null;     // accounts.balance_at, ISO
  stale: boolean;              // source !== 'manual' && (syncedAt null || older than 36 h)
  status: 'ok' | 'gap' | 'market' | 'no-data';
  gapCents: number | null;     // status 'gap': bank balance − ledger balance (signed, cents)
  since: string | null;        // 'YYYY-MM-DD' anchor day the check runs from
  balanceCents: number | null; // what the bank (or owner) says now
  ledgerCents: number | null;  // anchor balance + every non-pending txn posted after the anchor day
}

export interface ReconcileResult {
  accounts: ReconcileAccount[];
  fronted: { outstandingCents: number; count: number }; // SUM/COUNT of category 'money.reimbursable'; ≠ 0 means someone still owes (or was overpaid)
}

export function getReconcile(): Promise<ReconcileResult> {
  return request('/finance/reconcile');
}

/** Edit an account — manual accounts (a vehicle) take a new balance here. */
export async function updateFinanceAccount(
  id: string,
  body: { name?: string; type?: string; balanceCents?: number | null; excludeFromTotals?: boolean },
): Promise<FinanceAccount> {
  const res = await request<{ account: FinanceAccount }>(`/finance/accounts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  return res.account;
}

export async function deleteFinanceAccount(id: string): Promise<void> {
  await request(`/finance/accounts/${id}`, { method: 'DELETE' });
}

// --- Revenue (PEPM + MRR, hand-tracked income) ------------------------------

export interface PepmSettings {
  members: number;
  pepmCents: number;
  hourlyCents: number;
  hoursPerYear: number;
}
export interface MrrClient {
  name: string;
  cents: number;
}
export interface MrrSettings {
  clients: MrrClient[];
}
export function getRevenue(): Promise<{ pepm: PepmSettings; mrr: MrrSettings }> {
  return request('/revenue');
}
export function putPepm(body: PepmSettings): Promise<PepmSettings> {
  return request('/revenue/pepm', { method: 'PUT', body: JSON.stringify(body) });
}
export function putMrr(body: MrrSettings): Promise<MrrSettings> {
  return request('/revenue/mrr', { method: 'PUT', body: JSON.stringify(body) });
}

// --- Scribe (the floating "add something" chat) ------------------------------

export interface ScribeAction {
  tool: string;
  summary: string;
}
export interface ScribeTurn {
  role: 'user' | 'assistant';
  content: string;
  actions?: ScribeAction[];
}
export async function getScribeThread(): Promise<ScribeTurn[]> {
  const r = await request<{ turns: ScribeTurn[] }>('/scribe');
  return r.turns;
}
export function sendScribe(text: string): Promise<{ reply: string; actions: ScribeAction[] }> {
  return request('/scribe', { method: 'POST', body: JSON.stringify({ text }) });
}
export function clearScribeThread(): Promise<void> {
  return request('/scribe', { method: 'DELETE' });
}

// --- Contacts (agency client registry + message inbox) ------------------------

export type ContactKind = 'client' | 'person' | 'vendor';
export type HandleKind = 'phone' | 'email';
export interface ContactHandle {
  id?: number;
  kind: HandleKind;
  value: string;
  label?: string | null;
}
export interface Contact {
  id: number;
  kind: ContactKind;
  name: string;
  contactName: string | null;
  project: string | null;
  domain: string | null;
  staging: string | null;
  docker: string | null;
  notes: string | null;
  /** Clients only: act on their texts without asking and send the diff. */
  auto: boolean;
  handles: ContactHandle[];
  createdAt: string;
  updatedAt: string;
}
export type ContactInput = Omit<Contact, 'id' | 'createdAt' | 'updatedAt'>;

export type MessageChannel = 'imessage' | 'sms' | 'signal' | 'email';
export interface MessageAttachment {
  path?: string;
  mime?: string;
  local?: string;
}
export interface Message {
  id: number;
  channel: MessageChannel;
  externalId: string;
  contactId: number | null;
  handle: string;
  direction: 'in' | 'out';
  text: string;
  attachments: MessageAttachment[];
  sentAt: string;
}
/** An unlinked message may carry the sender's display name from the source device. */
export type InboxMessage = Message & { contactName?: string };

export async function getContacts(): Promise<Contact[]> {
  const res = await request<{ contacts: Contact[] }>('/contacts');
  return res.contacts;
}
export async function createContact(body: ContactInput): Promise<Contact> {
  const res = await request<{ contact: Contact }>('/contacts', { method: 'POST', body: JSON.stringify(body) });
  return res.contact;
}
/** Partial updates are fine; `handles`, when present, replaces the whole list. */
export async function updateContact(id: number, body: Partial<ContactInput>): Promise<Contact> {
  const res = await request<{ contact: Contact }>(`/contacts/${id}`, { method: 'PUT', body: JSON.stringify(body) });
  return res.contact;
}
export function deleteContact(id: number): Promise<void> {
  return request(`/contacts/${id}`, { method: 'DELETE' });
}
/** Newest first. */
export async function getContactMessages(id: number, limit = 50): Promise<Message[]> {
  const res = await request<{ messages: Message[] }>(`/contacts/${id}/messages${buildQuery({ limit })}`);
  return res.messages;
}
/** Messages from handles that match no contact, newest first. */
export async function getInboxMessages(limit = 100): Promise<InboxMessage[]> {
  const res = await request<{ messages: InboxMessage[] }>(`/messages${buildQuery({ limit, unlinked: 1 })}`);
  return res.messages;
}

// --- Money: splits (migration 0025) -------------------------------------------

export interface TransactionSplitLine {
  id: string;
  idx: number;
  category: string;
  /** Integer cents, same sign as the parent row. */
  amountCents: number;
  note: string | null;
}

/** Split one bank row across categories: ≥2 lines whose cents sum exactly to the row; `[]` removes the split. */
export async function setTransactionSplits(
  id: string,
  lines: Array<{ category: string; amountCents: number; note?: string | null }>,
): Promise<FinanceTransaction> {
  const res = await request<{ transaction: FinanceTransaction }>(`/finance/transactions/${encodeURIComponent(id)}/splits`, {
    method: 'PUT',
    body: JSON.stringify({ lines }),
  });
  return res.transaction;
}
